import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './RailTooltip.module.css';

const GAP = 8;

/**
 * The collapsed rail's hover label (Figma `22012:417341`, "Closed sidebar + tooltip"): the strip shows
 * glyphs only, so each one names itself beside the rail on hover or keyboard focus.
 *
 * Portalled to `<body>` because the rail clips its own overflow (`.orion-sidebar` is `overflow-x:
 * hidden`, which is what makes the width animation clean), so a tooltip drawn inside it would be cut
 * off at the rail's edge. The label is presentation only: every control it wraps carries its own
 * `aria-label`, so the tooltip is `aria-hidden` rather than a second announcement.
 */
//
// `placement="below"` is for a control inside the OPEN rail with a neighbour beside it (the Chats row's
// Search and New Chat), where a label at the side would cover the next button.
export function RailTooltip({ label, children, placement = 'side' }: { label: string; children: ReactNode; placement?: 'side' | 'below' }) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number } | null>(null);

  const show = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (placement === 'below') {
      setPos({ top: r.bottom + GAP, left: r.left + r.width / 2 });
      return;
    }
    const rtl = document.documentElement.dir === 'rtl';
    setPos(rtl ? { top: r.top + r.height / 2, right: window.innerWidth - r.left + GAP } : { top: r.top + r.height / 2, left: r.right + GAP });
  }, [placement]);
  const hide = useCallback(() => setPos(null), []);

  // A click that navigates or collapses moves the anchor, so a label left up would point at nothing.
  useLayoutEffect(() => {
    if (!pos) return undefined;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [pos, hide]);

  return (
    <span
      ref={anchorRef}
      className={styles.anchor}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={hide}
      onPointerDown={hide}
    >
      {children}
      {pos &&
        createPortal(
          <span aria-hidden className={`${styles.tooltip} ${placement === 'below' ? styles.below : ''}`} style={{ top: pos.top, left: pos.left, right: pos.right }}>
            {label}
          </span>,
          document.body,
        )}
    </span>
  );
}
